import { useEffect, useState, type ReactNode } from "react";
import { Loader2, Pencil } from "lucide-react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

export type InlineEditVariant =
  | "text"
  | "number"
  | "select"
  | "toggle"
  | "date";

export type InlineEditValue = string | number | boolean | null;

export interface InlineEditFieldProps {
  variant: InlineEditVariant;
  value: InlineEditValue;
  options?: Array<{ value: string; label: string }>;
  placeholder?: string;
  display?: ReactNode;
  readOnly?: boolean;
  onSave: (newValue: InlineEditValue) => Promise<void>;
}

export function InlineEditField({
  variant,
  value,
  options,
  placeholder,
  display,
  readOnly = false,
  onSave,
}: InlineEditFieldProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<InlineEditValue>(value);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  async function commit(newValue: InlineEditValue) {
    if (newValue === value) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(newValue);
      setEditing(false);
    } catch (err) {
      setError(extractMessage(err));
    } finally {
      setSaving(false);
    }
  }

  function cancel() {
    setDraft(value);
    setEditing(false);
    setError(null);
  }

  // TOGGLE — always rendered as Switch, saves on flip
  if (variant === "toggle") {
    return (
      <span className="inline-flex items-center gap-2">
        <Switch
          checked={!!value}
          disabled={readOnly || saving}
          onCheckedChange={(checked) => commit(checked)}
        />
        {saving && (
          <Loader2 className="h-3 w-3 animate-spin text-slate-400" />
        )}
        {error && (
          <span className="text-xs text-rose-600" title={error}>
            !
          </span>
        )}
      </span>
    );
  }

  // View mode (text/number/select/date) — same height as edit mode (h-8)
  if (!editing) {
    return (
      <button
        type="button"
        disabled={readOnly}
        onClick={() => setEditing(true)}
        className={cn(
          "group flex h-8 w-full min-w-0 items-center gap-2 rounded-md px-2 text-left",
          !readOnly && "hover:bg-slate-50",
          readOnly && "cursor-default",
        )}
      >
        <span className="min-w-0 flex-1 truncate">
          {display ?? renderDefault(value, variant, options)}
        </span>
        {!readOnly && (
          <Pencil className="h-3 w-3 shrink-0 text-slate-300 group-hover:text-slate-500" />
        )}
      </button>
    );
  }

  // Edit mode (text/number/select/date)
  return (
    <div className="flex items-center gap-2">
      {variant === "text" && (
        <Input
          autoFocus
          value={(draft as string | null) ?? ""}
          onChange={(e) =>
            setDraft(e.target.value === "" ? null : e.target.value)
          }
          onBlur={() => commit(draft)}
          onKeyDown={(e) => handleKey(e, () => commit(draft), cancel)}
          disabled={saving}
          placeholder={placeholder}
          className="h-8"
        />
      )}
      {variant === "number" && (
        <Input
          autoFocus
          type="number"
          inputMode="numeric"
          value={
            draft === null || draft === undefined ? "" : (draft as number)
          }
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "") {
              setDraft(null);
            } else {
              const n = Number(raw);
              setDraft(Number.isNaN(n) ? null : n);
            }
          }}
          onBlur={() => commit(draft)}
          onKeyDown={(e) => handleKey(e, () => commit(draft), cancel)}
          disabled={saving}
          placeholder={placeholder}
          className="h-8"
        />
      )}
      {variant === "date" && (
        <Input
          autoFocus
          type="date"
          value={(draft as string | null) ?? ""}
          onChange={(e) => setDraft(e.target.value || null)}
          onBlur={() => commit(draft)}
          onKeyDown={(e) => handleKey(e, () => commit(draft), cancel)}
          disabled={saving}
          className="h-8"
        />
      )}
      {variant === "select" && (
        <Select
          defaultOpen
          value={(draft as string | null) ?? ""}
          onValueChange={(v) => commit(v)}
          onOpenChange={(open) => {
            if (!open && !saving) {
              // Trigger only if no commit happened (closing without selecting).
              setTimeout(() => {
                setEditing((current) => (current ? false : current));
              }, 0);
            }
          }}
        >
          <SelectTrigger className="h-8" autoFocus>
            <SelectValue placeholder={placeholder ?? "Select…"} />
          </SelectTrigger>
          <SelectContent>
            {options?.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {saving && (
        <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-slate-400" />
      )}
      {error && (
        <span className="text-xs text-rose-600" title={error}>
          {error}
        </span>
      )}
    </div>
  );
}

function handleKey(
  e: React.KeyboardEvent,
  onEnter: () => void,
  onEscape: () => void,
) {
  if (e.key === "Enter") {
    e.preventDefault();
    onEnter();
  } else if (e.key === "Escape") {
    e.preventDefault();
    onEscape();
  }
}

function renderDefault(
  value: InlineEditValue,
  variant: InlineEditVariant,
  options?: Array<{ value: string; label: string }>,
): ReactNode {
  if (value == null || value === "") {
    return <span className="text-slate-400">—</span>;
  }
  if (variant === "select" && options) {
    return options.find((o) => o.value === value)?.label ?? String(value);
  }
  return String(value);
}

function extractMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0] as { msg?: string };
      if (first?.msg) return first.msg;
    }
  }
  if (err && typeof err === "object" && "message" in err) {
    return String((err as { message: unknown }).message);
  }
  return "Save failed";
}
